import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import AutoPublishSection from "@/components/AutoPublishSection";
import EmailCapture from "@/components/EmailCapture";
import TrailerModal from "@/components/TrailerModal";
import { useApp } from "@/context/AppContext";
import { paymentsAPI } from "@/lib/api";
import { PixelEvents } from "@/lib/pixel";
import { toast } from "sonner";
import {
    Sparkles, PenLine, User2, Calendar, TrendingUp, Wallet,
    ArrowRight, Check, Play, Shield, Timer, MinusCircle, PlusCircle,
    HelpCircle, Clock, Zap, Eye, EyeOff, Lock, Award,
    Hourglass, Rocket, Coins, Youtube, Link2, Package, Target, Calculator, Divide
} from "lucide-react";

const TXT = {
    fr: {
        // Hero
        hero_kicker: "LE DIRECTEUR MASQUÉ · SAISON 1",
        hero_h1_a: "Tout ton Short",
        hero_h1_b: "en quelques clics.",
        hero_h1_c: "Sans montrer ton visage.",
        hero_sub: "Idée, personnage, script, images et planning sur 30 jours : un studio IA guidé te livre tout, prêt à monter. Sans caméra. Sans expérience.",
        hero_cta: "Commencer maintenant · 9€",
        hero_secondary: "Voir en 40 secondes",
        hero_trust: "Accès immédiat · Sans engagement · Paiement sécurisé Stripe",

        // Problem
        prob_kicker: "01 · LE MUR INVISIBLE",
        prob_h2: "Tu veux gagner sur YouTube. Mais quelque chose te bloque.",
        prob_lead: "T'as vu passer 100 vidéos « comment j'ai fait 10K€ avec YouTube Shorts ». Tu sais que c'est possible. Mais chaque fois que tu essaies…",
        prob_pains: [
            "Tu ne sais pas quelle niche choisir. Trop de conseils contradictoires.",
            "Tu n'as ni caméra, ni micro, ni logiciel de montage.",
            "Tu n'aimes pas te filmer. L'idée même te bloque.",
            "Tu n'as pas le temps. Entre le boulot, la famille, ça ne rentre pas.",
            "Tu as essayé ChatGPT. Résultat : des scripts génériques, sans personnage, sans suite.",
            "Tu ne sais pas comment monter, publier, ni si c'est légal.",
        ],
        prob_close: "Le vrai problème ? Personne ne te livre le workflow complet. Juste des morceaux.",

        // Solution
        sol_kicker: "02 · LA MÉTHODE",
        sol_h2: "6 modules. Un workflow. Zéro friction.",
        sol_lead: "Le Directeur Masqué a construit un studio entier. Tu cliques, il produit.",

        // Use cases
        rev_kicker: "04 · CAS D'USAGE",
        rev_h2: "Trois façons de démarrer avec Shorts Factory.",
        rev_lead: "Des scénarios hypothétiques pour visualiser comment tu pourrais utiliser le studio dès ta première semaine.",
        rev_disclaimer_short: "Exemples illustratifs — pas de clients réels, pas de chiffres promis.",
        rev_disclaimer_long: "Exemples illustratifs — les résultats dépendent de votre contenu, de votre audience et de votre régularité.",
        rev_case_label: "Scénario",
        rev_cases: [
            {
                title: "Choisir sa niche sans se perdre",
                body: "Imagine que tu hésites entre trois passions : finance, cuisine simple, histoire. Le Directeur te pose quelques questions, croise tes affinités avec les angles qui fonctionnent en Shorts, et te propose un territoire éditorial clair à explorer en premier.",
            },
            {
                title: "Créer un personnage récurrent",
                body: "Tu ne veux pas montrer ton visage. Tu décris une ambiance et une voix ; le studio génère un personnage visuel cohérent qui pourra devenir la signature de ta chaîne, Short après Short.",
            },
            {
                title: "Planifier ses premiers Shorts",
                body: "Tu ouvres le calendrier 30 jours : chaque case propose un concept, un script et un angle. Tu réorganises, tu valides, et tu lances ta production en série sans jamais te demander « qu'est-ce que je publie demain ».",
            },
        ],

        // Pricing
        pri_kicker: "05 · TARIFS",
        pri_h2: "Une machine à contenu. Plusieurs projets. Des dizaines de Shorts.",
        pri_lead: "De la niche à la publication automatique, Shorts Factory vous accompagne pour transformer une idée en contenu publié.",
        pri_reco: "LE PLUS POPULAIRE",
        pri_one_name: "One Shot",
        pri_one_price: "9",
        pri_one_note: "Paiement unique",
        pri_one_sub: "Tout ce qu'il faut pour créer ton premier Short de A à Z.",
        pri_one_cta: "Créer mon premier Short — 9 €",
        pri_one_features: [
            "1 Short complet livré",
            "Recherche / choix de niche",
            "Génération d'idée + concept",
            "Script + personnage IA",
            "Production, prévisualisation, téléchargement",
            "Assistance du Directeur Masqué",
        ],
        pri_mo_name: "Creator",
        pri_mo_price: "39",
        pri_mo_note: "/mois · Sans engagement",
        pri_mo_sub: "Même fonctionnalité que One Shot, plus une véritable machine de production.",
        pri_mo_cta: "Lancer ma chaîne",
        pri_mo_features_head: "Même fonctionnalité que One Shot, plus :",
        pri_mo_features: [
            "30 Shorts/mois",
            "Projet de chaîne",
            "Calendrier éditorial 30 jours",
            "Production en série",
            "Bibliothèque de contenus + historique",
            "Viral Lab · Outils d'analyse et d'optimisation",
            "Outils de monétisation",
            "YouTube Import · Résumé et analyse",
            "Accompagnement continu et contextualisé du Directeur",
        ],
        pri_pack_name: "Capacité supplémentaire",
        pri_pack_price: "+19",
        pri_pack_note: "/mois · +30 Shorts",
        pri_pack_sub: "Utilise-les sur ta chaîne actuelle ou crée un nouveau projet.",
        pri_pack_cta: "Ajouter 30 Shorts",
        pri_pack_bullets: [
            "+30 Shorts/mois",
            "Empilable ×N (58€ · 77€ · 96€…)",
            "Réaffectable entre projets",
        ],
        pri_calc_kicker: "CALCULATEUR",
        pri_calc_h3: "Combien de Shorts veux-tu produire chaque mois ?",
        pri_calc_units_label: "Shorts par mois",
        pri_calc_price_label: "Total mensuel",
        pri_calc_hint: "Utilise ta capacité sur une seule chaîne, ou répartis-la entre plusieurs projets.",
        pri_calc_cta: "S'abonner à ce niveau",
        pri_soon: "Bientôt disponible",

        // FAQ
        faq_kicker: "07 · OBJECTIONS",
        faq_h2: "Les questions que tout le monde se pose.",

        // ROI
        roi_kicker: "06 · ROI",
        roi_h2: "39 € par mois. Et si votre contenu pouvait payer l'abonnement ?",
        roi_lead: "Produisez jusqu'à 30 Shorts par mois pour seulement 39 € — soit environ 1,30 € par Short. Besoin de plus ? Chaque pack +19 € ajoute 30 Shorts.",
        roi_calc_a: "39 €",
        roi_calc_b: "30 Shorts",
        roi_calc_c: "1,30 €",
        roi_calc_label_a: "par mois",
        roi_calc_label_b: "produits",
        roi_calc_label_c: "par Short",
        roi_note: "Le véritable avantage n'est pas seulement le coût par vidéo. C'est le temps économisé et la capacité à publier régulièrement, sans transformer chaque Short en plusieurs heures de travail.",
        roi_dim_title: "3 dimensions du ROI",
        roi_time_title: "Récupérez votre temps",
        roi_time_desc: "Idées, scripts, personnages, production et publication sont réunis dans un seul workflow. Consacrez moins de temps aux tâches répétitives et davantage à votre audience et votre business.",
        roi_reach_title: "Plus de contenu. Plus d'opportunités.",
        roi_reach_desc: "Jusqu'à 60 Shorts par mois vous permettent de tester davantage de sujets, hooks, formats et angles pour identifier ce qui fonctionne auprès de votre audience.",
        roi_biz_title: "Transformez votre audience en revenus",
        roi_biz_desc: "Une audience peut être monétisée de plusieurs façons : publicité YouTube, affiliation, produits, services, sponsoring ou génération de prospects.",
        roi_paths_kicker: "COMMENT RÉCUPÉRER VOS 39 €",
        roi_paths_h3: "Votre abonnement peut être rentabilisé de plusieurs façons",
        roi_paths: [
            { title: "YouTube", desc: "Développez votre chaîne et, lorsque vous remplissez les conditions d'éligibilité, accédez aux fonctionnalités de monétisation YouTube." },
            { title: "Affiliation", desc: "Recommandez des produits ou services pertinents à votre audience et percevez des commissions sur les conversions." },
            { title: "Produits & services", desc: "Utilisez vos Shorts pour attirer des clients vers vos produits, formations, logiciels ou services." },
            { title: "Leads", desc: "Transformez votre contenu en canal d'acquisition pour générer des prospects pour votre activité." },
        ],
        roi_examples_kicker: "QUELQUES EXEMPLES",
        roi_examples_h3: "Illustrations mathématiques",
        roi_examples: [
            { top: "Produit à 49 €", bot: "2 ventes = 98 €" },
            { top: "Commission d'affiliation à 20 €", bot: "4 conversions = 80 €" },
            { top: "Lead valorisé à 100 €", bot: "1 lead = 100 €" },
        ],
        roi_examples_disclaimer: "Exemples mathématiques illustratifs. Les revenus réels dépendent de votre audience, de votre niche, de votre offre, de vos taux de conversion et de votre capacité à monétiser votre trafic.",
        sim_kicker: "SIMULATEUR",
        sim_h3: "Ajustez vos hypothèses. Voyez votre potentiel.",
        sim_price_label: "Prix moyen d'une vente (€)",
        sim_conv_label: "Conversions par mois",
        sim_revenue_label: "Revenu illustratif mensuel",
        sim_ratio_label: "vs. 39 € d'abonnement",
        sim_disclaimer: "Simulation illustrative. Les revenus réels dépendent de votre audience, de vos taux de conversion et de votre capacité à monétiser votre trafic.",
        roi_closing_a: "Vous ne payez pas 39 € pour 30 vidéos.",
        roi_closing_b: "Vous investissez 39 € pour construire une machine de contenu capable de créer jusqu'à 30 opportunités d'acquisition par mois — scalable pack après pack.",
        roi_closing_verbs: "Créez. Publiez. Développez votre audience. Monétisez.",
        roi_cta_primary: "Lancer ma chaîne · 39 €/mois",
        roi_cta_secondary: "Tester avec 1 Short · 9 €",

        // Final CTA
        end_kicker: "08 · À TOI",
        end_h2: "Le Directeur Masqué t'attend dans le studio.",
        end_sub: "Tu peux fermer cet onglet et retenter dans 6 mois. Ou tu peux poser 9 €, rejoindre les créateurs déjà actifs, et publier ton premier Short cette semaine.",
        end_cta: "Rejoindre le studio · 9€",
        end_proof: "Studio IA complet · Sans caméra · Sans montage",
        end_bullets: [
            "Accès instantané au studio",
            "Tous les modules débloqués",
            "Mises à jour de sécurité continues",
            "Support par email · Réponse < 24h",
        ],
    },
    en: {
        hero_kicker: "THE MASKED DIRECTOR · SEASON 1",
        hero_h1_a: "Your whole Short",
        hero_h1_b: "in a few clicks.",
        hero_h1_c: "Without showing your face.",
        hero_sub: "Idea, character, script, images and a 30-day plan: a guided AI studio delivers it all, ready to edit. No camera. No experience.",
        hero_cta: "Start now · €9",
        hero_secondary: "Watch in 40 seconds",
        hero_trust: "Instant access · No commitment · Secure Stripe checkout",

        prob_kicker: "01 · THE INVISIBLE WALL",
        prob_h2: "You want to win on YouTube. But something blocks you.",
        prob_lead: "You've seen 100 videos titled \"how I made $10K with Shorts\". You know it's possible. But every time you try…",
        prob_pains: [
            "You don't know which niche to pick. Too much contradictory advice.",
            "No camera, no mic, no editing software.",
            "You hate filming yourself. The idea alone blocks you.",
            "No time. Work, family, it doesn't fit.",
            "You tried ChatGPT. Result: bland scripts, no character, no series.",
            "You don't know how to edit, publish, or if it's even legal.",
        ],
        prob_close: "The real problem? Nobody ships you the whole workflow. Just fragments.",

        sol_kicker: "02 · THE METHOD",
        sol_h2: "6 modules. One workflow. Zero friction.",
        sol_lead: "The Masked Director built an entire studio. You click, he produces.",

        rev_kicker: "04 · USE CASES",
        rev_h2: "Three ways to get started with Shorts Factory.",
        rev_lead: "Hypothetical scenarios to picture how you could use the studio in your very first week.",
        rev_disclaimer_short: "Illustrative examples — no real customers, no promised numbers.",
        rev_disclaimer_long: "Illustrative examples — results depend on your content, your audience and your consistency.",
        rev_case_label: "Scenario",
        rev_cases: [
            {
                title: "Pick a niche without getting lost",
                body: "Imagine you're torn between three passions: finance, easy cooking, history. The Director asks a few questions, cross-checks your affinities with angles that work in Shorts, and hands you a clear editorial territory to explore first.",
            },
            {
                title: "Build a recurring character",
                body: "You don't want to show your face. You describe a vibe and a voice; the studio generates a consistent visual character that can become your channel's signature, Short after Short.",
            },
            {
                title: "Plan your first Shorts",
                body: "Open the 30-day calendar: each slot proposes a concept, a script and an angle. You rearrange, approve, and launch batch production without ever wondering \"what do I post tomorrow\".",
            },
        ],

        pri_kicker: "05 · PRICING",
        pri_h2: "One content machine. Multiple projects. Dozens of Shorts.",
        pri_lead: "From niche to auto-publish, Shorts Factory walks you from an idea to published content.",
        pri_reco: "MOST POPULAR",
        pri_one_name: "One Shot",
        pri_one_price: "9",
        pri_one_note: "One-time payment",
        pri_one_sub: "Everything you need to build your first Short end-to-end.",
        pri_one_cta: "Create my first Short — €9",
        pri_one_features: [
            "1 full Short delivered",
            "Niche research + selection",
            "Idea + concept generation",
            "Script + AI character",
            "Production, preview, download",
            "Masked Director assistance",
        ],
        pri_mo_name: "Creator",
        pri_mo_price: "39",
        pri_mo_note: "/mo · Cancel anytime",
        pri_mo_sub: "Everything from One Shot, plus a real production machine.",
        pri_mo_cta: "Launch my channel",
        pri_mo_features_head: "Everything in One Shot, plus:",
        pri_mo_features: [
            "30 Shorts/month",
            "Channel project",
            "30-day editorial calendar",
            "Series production",
            "Content library + history",
            "Viral Lab · Analytics & optimization",
            "Monetization tools",
            "YouTube Import · Summarize & analyze",
            "Continuous, project-aware Director",
        ],
        pri_pack_name: "Extra capacity",
        pri_pack_price: "+19",
        pri_pack_note: "/mo · +30 Shorts",
        pri_pack_sub: "Use them on your current channel or spin up a new project.",
        pri_pack_cta: "Add 30 Shorts",
        pri_pack_bullets: [
            "+30 Shorts/month",
            "Stackable ×N (€58 · €77 · €96…)",
            "Reallocate across projects",
        ],
        pri_calc_kicker: "CALCULATOR",
        pri_calc_h3: "How many Shorts do you want to produce each month?",
        pri_calc_units_label: "Shorts per month",
        pri_calc_price_label: "Monthly total",
        pri_calc_hint: "Use your capacity on a single channel or split it across projects.",
        pri_calc_cta: "Subscribe at this level",
        pri_soon: "Coming soon",

        faq_kicker: "07 · OBJECTIONS",
        faq_h2: "The questions everyone has.",

        // ROI
        roi_kicker: "06 · ROI",
        roi_h2: "€39 a month. What if your content could pay for the subscription?",
        roi_lead: "Produce up to 30 Shorts per month for just €39 — that's about €1.30 per Short. Need more? Each +€19 pack adds 30 Shorts.",
        roi_calc_a: "€39",
        roi_calc_b: "30 Shorts",
        roi_calc_c: "€1.30",
        roi_calc_label_a: "per month",
        roi_calc_label_b: "produced",
        roi_calc_label_c: "per Short",
        roi_note: "The real edge isn't just cost-per-video. It's the time saved and the ability to publish consistently, without turning every Short into hours of work.",
        roi_dim_title: "3 ROI dimensions",
        roi_time_title: "Get your time back",
        roi_time_desc: "Ideas, scripts, characters, production and publishing all live in one workflow. Spend less time on repetitive tasks and more on your audience and your business.",
        roi_reach_title: "More content. More opportunities.",
        roi_reach_desc: "Up to 60 Shorts a month let you test more topics, hooks, formats and angles to figure out what actually works with your audience.",
        roi_biz_title: "Turn your audience into revenue",
        roi_biz_desc: "An audience can be monetized in many ways: YouTube ads, affiliate marketing, products, services, sponsorships or lead generation.",
        roi_paths_kicker: "HOW YOU CAN RECOUP €39",
        roi_paths_h3: "Your subscription can pay for itself in several ways",
        roi_paths: [
            { title: "YouTube", desc: "Grow your channel and, once you meet the eligibility criteria, unlock YouTube's monetization features." },
            { title: "Affiliate", desc: "Recommend relevant products or services to your audience and earn commissions on conversions." },
            { title: "Products & services", desc: "Use your Shorts to drive customers to your products, courses, software or services." },
            { title: "Leads", desc: "Turn your content into an acquisition channel to generate qualified leads for your business." },
        ],
        roi_examples_kicker: "A FEW EXAMPLES",
        roi_examples_h3: "Illustrative math",
        roi_examples: [
            { top: "Product at €49", bot: "2 sales = €98" },
            { top: "Affiliate commission at €20", bot: "4 conversions = €80" },
            { top: "Lead valued at €100", bot: "1 lead = €100" },
        ],
        roi_examples_disclaimer: "Illustrative math examples. Real revenue depends on your audience, niche, offer, conversion rates and your ability to monetize your traffic.",
        sim_kicker: "SIMULATOR",
        sim_h3: "Adjust your assumptions. See your potential.",
        sim_price_label: "Average price per sale (€)",
        sim_conv_label: "Conversions per month",
        sim_revenue_label: "Illustrative monthly revenue",
        sim_ratio_label: "vs. €39 subscription",
        sim_disclaimer: "Illustrative simulation. Real revenue depends on your audience, your conversion rates and your ability to monetize your traffic.",
        roi_closing_a: "You're not paying €39 for 30 videos.",
        roi_closing_b: "You're investing €39 to build a content machine capable of creating up to 30 acquisition opportunities a month — scalable pack after pack.",
        roi_closing_verbs: "Create. Publish. Grow your audience. Monetize.",
        roi_cta_primary: "Launch my channel · €39/mo",
        roi_cta_secondary: "Test with 1 Short · €9",

        end_kicker: "08 · YOUR MOVE",
        end_h2: "The Masked Director is waiting in the studio.",
        end_sub: "You can close this tab and try again in 6 months. Or you can drop €9, join active creators, and publish your first Short this week.",
        end_cta: "Join the studio · €9",
        end_proof: "Complete AI studio · No camera · No editing",
        end_bullets: [
            "Instant studio access",
            "All modules unlocked",
            "Continuous security updates",
            "Email support · Reply within 24h",
        ],
    },
    es: {
        hero_kicker: "EL DIRECTOR ENMASCARADO · TEMPORADA 1",
        hero_h1_a: "Todo tu Short",
        hero_h1_b: "en unos clics.",
        hero_h1_c: "Sin mostrar tu cara.",
        hero_sub: "Idea, personaje, guion, imágenes y planificación de 30 días: un estudio de IA guiado te lo entrega todo, listo para editar. Sin cámara. Sin experiencia.",
        hero_cta: "Empezar ahora · 9€",
        hero_secondary: "Ver en 40 segundos",
        hero_trust: "Acceso instantáneo · Sin permanencia · Pago seguro Stripe",

        prob_kicker: "01 · EL MURO INVISIBLE",
        prob_h2: "Quieres ganar en YouTube. Pero algo te bloquea.",
        prob_lead: "Has visto 100 vídeos titulados «cómo gané 10.000€ con Shorts». Sabes que es posible. Pero cada vez que lo intentas…",
        prob_pains: [
            "No sabes qué nicho elegir. Demasiados consejos contradictorios.",
            "Sin cámara, sin micro, sin software de edición.",
            "Odias grabarte. Solo la idea ya te bloquea.",
            "Sin tiempo. Entre trabajo y familia, no cabe.",
            "Probaste ChatGPT. Resultado: guiones genéricos, sin personaje, sin serie.",
            "No sabes cómo editar, publicar, ni si es legal.",
        ],
        prob_close: "¿El verdadero problema? Nadie te entrega el flujo completo. Solo trozos.",

        sol_kicker: "02 · EL MÉTODO",
        sol_h2: "6 módulos. Un flujo. Cero fricción.",
        sol_lead: "El Director Enmascarado construyó un estudio entero. Tú haces clic, él produce.",

        rev_kicker: "04 · CASOS DE USO",
        rev_h2: "Tres formas de empezar con Shorts Factory.",
        rev_lead: "Escenarios hipotéticos para visualizar cómo podrías usar el estudio desde tu primera semana.",
        rev_disclaimer_short: "Ejemplos ilustrativos — sin clientes reales ni cifras prometidas.",
        rev_disclaimer_long: "Ejemplos ilustrativos — los resultados dependen de tu contenido, tu audiencia y tu constancia.",
        rev_case_label: "Escenario",
        rev_cases: [
            {
                title: "Elegir un nicho sin perderte",
                body: "Imagina que dudas entre tres pasiones: finanzas, cocina sencilla, historia. El Director te hace unas preguntas, cruza tus afinidades con los ángulos que funcionan en Shorts y te propone un territorio editorial claro para explorar primero.",
            },
            {
                title: "Crear un personaje recurrente",
                body: "No quieres mostrar tu rostro. Describes un ambiente y una voz; el estudio genera un personaje visual coherente que puede convertirse en la firma de tu canal, Short tras Short.",
            },
            {
                title: "Planificar tus primeros Shorts",
                body: "Abres el calendario de 30 días: cada casilla propone un concepto, un guion y un ángulo. Reorganizas, validas y lanzas la producción en serie sin preguntarte nunca «¿qué publico mañana?».",
            },
        ],

        pri_kicker: "05 · PRECIOS",
        pri_h2: "Una máquina de contenido. Varios proyectos. Docenas de Shorts.",
        pri_lead: "Del nicho a la publicación automática, Shorts Factory te acompaña para convertir una idea en contenido publicado.",
        pri_reco: "EL MÁS POPULAR",
        pri_one_name: "One Shot",
        pri_one_price: "9",
        pri_one_note: "Pago único",
        pri_one_sub: "Todo lo necesario para crear tu primer Short de la A a la Z.",
        pri_one_cta: "Crear mi primer Short — 9 €",
        pri_one_features: [
            "1 Short completo entregado",
            "Búsqueda y elección de nicho",
            "Generación de idea + concepto",
            "Guion + personaje IA",
            "Producción, vista previa, descarga",
            "Asistencia del Director Enmascarado",
        ],
        pri_mo_name: "Creator",
        pri_mo_price: "39",
        pri_mo_note: "/mes · Cancela cuando quieras",
        pri_mo_sub: "Lo mismo que One Shot, más una auténtica máquina de producción.",
        pri_mo_cta: "Lanzar mi canal",
        pri_mo_features_head: "Lo mismo que One Shot, más:",
        pri_mo_features: [
            "30 Shorts/mes",
            "Proyecto de canal",
            "Calendario editorial de 30 días",
            "Producción en serie",
            "Biblioteca de contenidos + historial",
            "Viral Lab · Analítica y optimización",
            "Herramientas de monetización",
            "YouTube Import · Resumen y análisis",
            "Acompañamiento continuo y contextualizado del Director",
        ],
        pri_pack_name: "Capacidad extra",
        pri_pack_price: "+19",
        pri_pack_note: "/mes · +30 Shorts",
        pri_pack_sub: "Úsalos en tu canal actual o crea un nuevo proyecto.",
        pri_pack_cta: "Añadir 30 Shorts",
        pri_pack_bullets: [
            "+30 Shorts/mes",
            "Apilable ×N (58 € · 77 € · 96 €…)",
            "Reasignable entre proyectos",
        ],
        pri_calc_kicker: "CALCULADORA",
        pri_calc_h3: "¿Cuántos Shorts quieres producir cada mes?",
        pri_calc_units_label: "Shorts al mes",
        pri_calc_price_label: "Total mensual",
        pri_calc_hint: "Usa tu capacidad en un solo canal o repártela entre varios proyectos.",
        pri_calc_cta: "Suscribirse a este nivel",
        pri_soon: "Próximamente",

        faq_kicker: "07 · OBJECIONES",
        faq_h2: "Las preguntas que todos se hacen.",

        // ROI
        roi_kicker: "06 · ROI",
        roi_h2: "39 € al mes. ¿Y si tu contenido pudiera pagar la suscripción?",
        roi_lead: "Produce hasta 30 Shorts al mes por solo 39 € — aproximadamente 1,30 € por Short. ¿Necesitas más? Cada pack +19 € añade 30 Shorts.",
        roi_calc_a: "39 €",
        roi_calc_b: "30 Shorts",
        roi_calc_c: "1,30 €",
        roi_calc_label_a: "al mes",
        roi_calc_label_b: "producidos",
        roi_calc_label_c: "por Short",
        roi_note: "La verdadera ventaja no es solo el coste por vídeo. Es el tiempo ahorrado y la capacidad de publicar con constancia, sin convertir cada Short en horas de trabajo.",
        roi_dim_title: "3 dimensiones del ROI",
        roi_time_title: "Recupera tu tiempo",
        roi_time_desc: "Ideas, guiones, personajes, producción y publicación viven en un solo flujo. Dedica menos tiempo a tareas repetitivas y más a tu audiencia y a tu negocio.",
        roi_reach_title: "Más contenido. Más oportunidades.",
        roi_reach_desc: "Hasta 60 Shorts al mes te permiten probar más temas, hooks, formatos y ángulos para identificar qué funciona con tu audiencia.",
        roi_biz_title: "Convierte tu audiencia en ingresos",
        roi_biz_desc: "Una audiencia se puede monetizar de muchas formas: publicidad de YouTube, afiliación, productos, servicios, patrocinios o generación de leads.",
        roi_paths_kicker: "CÓMO RECUPERAR TUS 39 €",
        roi_paths_h3: "Tu suscripción se puede rentabilizar de varias formas",
        roi_paths: [
            { title: "YouTube", desc: "Haz crecer tu canal y, cuando cumplas los requisitos de elegibilidad, desbloquea las funciones de monetización de YouTube." },
            { title: "Afiliación", desc: "Recomienda productos o servicios relevantes a tu audiencia y recibe comisiones por las conversiones." },
            { title: "Productos y servicios", desc: "Usa tus Shorts para atraer clientes a tus productos, formaciones, software o servicios." },
            { title: "Leads", desc: "Convierte tu contenido en un canal de adquisición para generar leads cualificados para tu negocio." },
        ],
        roi_examples_kicker: "ALGUNOS EJEMPLOS",
        roi_examples_h3: "Ilustraciones matemáticas",
        roi_examples: [
            { top: "Producto a 49 €", bot: "2 ventas = 98 €" },
            { top: "Comisión de afiliación a 20 €", bot: "4 conversiones = 80 €" },
            { top: "Lead valorado en 100 €", bot: "1 lead = 100 €" },
        ],
        roi_examples_disclaimer: "Ejemplos matemáticos ilustrativos. Los ingresos reales dependen de tu audiencia, tu nicho, tu oferta, tus tasas de conversión y tu capacidad para monetizar tu tráfico.",
        sim_kicker: "SIMULADOR",
        sim_h3: "Ajusta tus hipótesis. Descubre tu potencial.",
        sim_price_label: "Precio medio por venta (€)",
        sim_conv_label: "Conversiones al mes",
        sim_revenue_label: "Ingreso ilustrativo mensual",
        sim_ratio_label: "vs. 39 € de suscripción",
        sim_disclaimer: "Simulación ilustrativa. Los ingresos reales dependen de tu audiencia, tus tasas de conversión y tu capacidad para monetizar tu tráfico.",
        roi_closing_a: "No pagas 39 € por 30 vídeos.",
        roi_closing_b: "Inviertes 39 € para construir una máquina de contenido capaz de generar hasta 30 oportunidades de captación al mes — escalable pack a pack.",
        roi_closing_verbs: "Crea. Publica. Haz crecer tu audiencia. Monetiza.",
        roi_cta_primary: "Lanzar mi canal · 39 €/mes",
        roi_cta_secondary: "Probar con 1 Short · 9 €",

        end_kicker: "08 · TU TURNO",
        end_h2: "El Director Enmascarado te espera en el estudio.",
        end_sub: "Puedes cerrar esta pestaña y reintentarlo en 6 meses. O poner 9 €, unirte a creadores ya activos, y publicar tu primer Short esta semana.",
        end_cta: "Entrar al estudio · 9€",
        end_proof: "Estudio IA completo · Sin cámara · Sin edición",
        end_bullets: [
            "Acceso instantáneo al estudio",
            "Todos los módulos desbloqueados",
            "Actualizaciones de seguridad continuas",
            "Soporte por email · Respuesta < 24h",
        ],
    },
};

const MODULES = [
    { id: "quiz",     icon: Sparkles,    fr: ["Quiz Niche", "En 4 questions → ton concept + 30 idées prêtes à tourner."], en: ["Niche Quiz", "In 4 questions → your concept + 30 ready-to-shoot ideas."] },
    { id: "scripts",  icon: PenLine,     fr: ["Générateur de scripts", "Hook, storyboard, voix off, hashtags — un clic."], en: ["Script generator", "Hook, storyboard, voice-over, hashtags — one click."] },
    { id: "character",icon: User2,       fr: ["Kit personnage", "Un héros récurrent, cohérent, épisode après épisode."], en: ["Character kit", "One recurring hero, consistent, episode after episode."] },
    { id: "calendar", icon: Calendar,    fr: ["Calendrier 30 jours", "30 épisodes connectés. Ta chaîne pense pour toi."], en: ["30-day calendar", "30 connected episodes. The channel thinks for you."] },
    { id: "viral",    icon: TrendingUp,  fr: ["Viral Lab", "Colle un Short qui marche → 10 concepts originaux."], en: ["Viral Lab", "Paste a working Short → 10 original concepts."] },
    { id: "money",    icon: Wallet,      fr: ["Roadmap monétisation", "0 → 100k abonnés. Les actions précises par palier."], en: ["Monetization roadmap", "0 → 100k subs. The precise moves per tier."] },
];

const USE_CASE_ICONS = [Target, User2, Calendar];
const USE_CASE_COLORS = ["#FF4D00", "#0099FF", "#FFB800"];

const FAQ = {
    fr: [
        { q: "Je n'ai aucune compétence, ça peut vraiment marcher ?", a: "Oui. Shorts Factory a été bâti pour quelqu'un qui n'a jamais écrit, monté ou utilisé une IA. Tu réponds à un quiz, tu cliques « générer », l'usine fait le reste. Si tu sais écrire un email, tu sais utiliser le studio." },
        { q: "Sans caméra, sans micro, sans logiciel — vraiment ?", a: "Vraiment. Le Directeur Masqué a été conçu pour rester anonyme. Personnages animés, voix générée par IA, montage guidé par templates 9:16 prêts à l'emploi. Ton téléphone suffit — même le vieux modèle qui traîne au fond du tiroir." },
        { q: "Combien de temps par jour faut-il investir ?", a: "15 minutes suffisent pour générer un épisode complet (script + storyboard + hashtags). Compter 20 à 30 minutes de plus pour le montage sur CapCut. Total : moins d'une heure par jour, planifiable en une seule session hebdo." },
        { q: "C'est légal ? Les IA, les droits, la monétisation YouTube ?", a: "Oui. Tu es 100% propriétaire des scripts, personnages et vidéos générés par le studio. Shorts Factory n'utilise aucune image protégée ni aucune voix humaine clonée. La monétisation YouTube reste soumise aux règles officielles de YouTube (audience, originalité, contenu réutilisé) : chaque chaîne reste évaluée au cas par cas par YouTube, Shorts Factory ne la garantit pas." },
        { q: "Un support est inclus si je bloque ?", a: "Oui. Un support par email est inclus (adresse publiée dès la mise en service de notre boîte dédiée). Le studio contient aussi des guides pas-à-pas et le Directeur Masqué (chat IA contextuel) pour répondre à tes questions à l'intérieur de l'application." },
    ],
    en: [
        { q: "I have zero skills, will this really work?", a: "Yes. Shorts Factory was built for someone who has never written, edited or used AI. You answer a quiz, click \"generate\", the factory does the rest. If you can write an email, you can use the studio." },
        { q: "No camera, no mic, no software — really?", a: "Really. The Masked Director was designed to keep you anonymous. Animated characters, AI-generated voice, guided 9:16 templates. Your phone is enough — even the old one in the drawer." },
        { q: "How much time per day does this take?", a: "15 minutes are enough to generate a full episode (script + storyboard + hashtags). 20-30 extra minutes for CapCut editing. Total: under an hour per day, or one weekly batch session." },
        { q: "Is it legal? The AI, the rights, YouTube monetization?", a: "Yes. You own 100% of the scripts, characters and videos generated by the studio. Shorts Factory uses no protected images and no cloned human voices. YouTube monetization remains subject to YouTube's official rules (audience, originality, reused content): each channel is evaluated case by case by YouTube — Shorts Factory does not guarantee it." },
        { q: "Is support included if I get stuck?", a: "Yes. Email support is included (address will be published as soon as our dedicated inbox is live). The studio also ships with step-by-step guides and the Masked Director (contextual AI chat) that answers your questions directly inside the app." },
    ],
};

const DIRECTOR_IMG = "https://static.prod-images.emergentagent.com/jobs/47063229-ef83-41b9-9c5c-31aac1a4917b/images/d4c4ab4650f82bd7acd152f528026309c46bbd2c30948d4c77c19787b5bfc699.jpeg";

export default function Landing() {
    const { lang, user } = useApp();
    const t = TXT[lang] || TXT.fr;
    const navigate = useNavigate();
    const [trailerOpen, setTrailerOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(0);
    const [simPrice, setSimPrice] = useState(49);
    const [simConv, setSimConv] = useState(4);
    const [calcUnits, setCalcUnits] = useState(2);
    const simRevenue = simPrice * simConv;
    const simRatio = simRevenue / 39;

    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const ref = url.searchParams.get("ref");
            if (ref && /^[a-z0-9]{4,32}$/i.test(ref)) localStorage.setItem("sf_ref", ref.toLowerCase());
        } catch { /* ignore */ }
        if (window.location.hash) {
            const id = window.location.hash.slice(1);
            const el = document.getElementById(id);
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        }
    }, []);

    const buyPlan = async (planId, units = 1) => {
        if (!user) { navigate("/auth?mode=register"); return; }
        const totalPrice = planId === "oneshot" ? 9 : (39 + Math.max(0, units - 1) * 19);
        PixelEvents.initiateCheckout(planId, totalPrice);
        try {
            const { checkout_url } = await paymentsAPI.checkout(planId, units);
            window.location.href = checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout error");
        }
    };

    return (
        <div className="min-h-screen bg-[#050914] text-slate-100 selection:bg-[#FF4D00] selection:text-black">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden noise-overlay">
                <div className="absolute inset-0 studio-grid opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050914]/70 to-[#050914]" />
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#FF4D00]/12 blur-[120px]" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-20 pb-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF4D00]/30 bg-black/40">
                                <EyeOff className="w-3 h-3 text-[#FF4D00]" />
                                <span className="overline text-[#FF4D00]">{t.hero_kicker}</span>
                            </div>

                            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.92] tracking-tight">
                                {t.hero_h1_a}<br />
                                <span className="flame-text">{t.hero_h1_b}</span><br />
                                <span className="text-slate-300">{t.hero_h1_c}</span>
                            </h1>

                            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
                                {t.hero_sub}
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <button
                                    data-testid="hero-primary-cta"
                                    onClick={() => buyPlan("oneshot")}
                                    className="flame-gradient text-black font-semibold px-6 py-3.5 rounded-md flex items-center gap-2 kinetic flame-glow"
                                >
                                    {t.hero_cta} <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    data-testid="hero-watch-trailer"
                                    onClick={() => { PixelEvents.viewContent("trailer"); setTrailerOpen(true); }}
                                    className="px-4 py-3.5 rounded-md text-slate-200 hover:text-white kinetic flex items-center gap-2 group"
                                >
                                    <span className="w-9 h-9 rounded-full border border-[#FF4D00]/40 group-hover:border-[#FF4D00] group-hover:bg-[#FF4D00]/10 grid place-items-center kinetic">
                                        <Play className="w-3.5 h-3.5 text-[#FF4D00] ml-0.5" fill="currentColor" />
                                    </span>
                                    <span className="text-sm">{t.hero_secondary}</span>
                                </button>
                            </div>

                            <div className="mt-6 flex items-center gap-2 text-xs font-mono-sf text-slate-500 uppercase tracking-widest">
                                <Shield className="w-3.5 h-3.5 text-[#10B981]" />
                                <span>{t.hero_trust}</span>
                            </div>
                        </div>

                        <div className="lg:col-span-5 flex justify-center relative">
                            <div className="absolute -inset-8 flame-gradient rounded-full blur-3xl opacity-25" />
                            <div className="relative">
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#050914] via-transparent to-transparent pointer-events-none z-10" />
                                <img
                                    src={DIRECTOR_IMG}
                                    alt="Le Directeur Masqué"
                                    data-testid="hero-director-image"
                                    loading="eager"
                                    decoding="async"
                                    className="relative w-72 sm:w-80 lg:w-96 aspect-[4/5] object-cover rounded-2xl border border-[#253046] shadow-[0_20px_80px_-20px_rgba(255,77,0,0.5)]"
                                />
                                <div className="absolute -bottom-3 -right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/80 border border-[#FF4D00]/40 backdrop-blur">
                                    <span className="w-1.5 h-1.5 bg-[#FF4D00] rounded-full rec-dot" />
                                    <span className="text-[10px] font-mono-sf text-[#FF4D00] uppercase tracking-widest">Season 1</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROBLEM */}
            <section className="relative py-24 bg-[#050914] border-t border-[#253046]">
                <div className="max-w-4xl mx-auto px-4 sm:px-8">
                    <div className="overline">{t.prob_kicker}</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">
                        {t.prob_h2}
                    </h2>
                    <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed">{t.prob_lead}</p>

                    <ul className="mt-10 space-y-4">
                        {t.prob_pains.map((p, i) => (
                            <li key={i} className="flex items-start gap-3 p-4 rounded border-l-2 border-[#FF4D00]/50 bg-gradient-to-r from-[#161D2A] to-[#080B10]">
                                <MinusCircle className="w-5 h-5 text-[#FF4D00] shrink-0 mt-0.5" />
                                <span className="text-slate-200 text-sm sm:text-base leading-relaxed">{p}</span>
                            </li>
                        ))}
                    </ul>

                    <p className="mt-10 text-lg italic text-slate-300 border-l-2 border-[#FF4D00] pl-4">
                        « {t.prob_close} »
                    </p>
                </div>
            </section>

            {/* SOLUTION */}
            <section id="modules" className="relative py-24 border-t border-[#253046] bg-gradient-to-b from-[#050914] to-[#080B10]">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <div className="max-w-2xl">
                        <div className="overline">{t.sol_kicker}</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">{t.sol_h2}</h2>
                        <p className="mt-4 text-slate-400">{t.sol_lead}</p>
                    </div>

                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {MODULES.map((m, i) => {
                            const [title, desc] = lang === "fr" ? m.fr : m.en;
                            const Icon = m.icon;
                            return (
                                <div key={m.id} data-testid={`module-card-${m.id}`} className="group relative bg-[#0B111B] border border-[#253046] hover:border-[#FF4D00]/50 rounded-lg p-6 kinetic overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#FF4D00]/5 group-hover:bg-[#FF4D00]/15 transition-colors" />
                                    <div className="font-mono-sf text-[10px] text-slate-500">0{i + 1}</div>
                                    <Icon className="w-8 h-8 mt-3 text-[#FF4D00]" strokeWidth={1.6} />
                                    <div className="font-display uppercase mt-4 text-xl font-bold">{title}</div>
                                    <div className="text-sm text-slate-400 mt-2 leading-relaxed">{desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* AUTOPUBLISH */}
            <AutoPublishSection lang={lang} onJoin={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} />

            {/* USE CASES */}
            <section className="relative py-24 border-t border-[#253046]" data-testid="use-cases-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <div className="max-w-2xl">
                        <div className="overline">{t.rev_kicker}</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">{t.rev_h2}</h2>
                        <p className="mt-4 text-slate-400">{t.rev_lead}</p>
                        <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF4D00]/30 bg-[#FF4D00]/5" data-testid="use-cases-disclaimer-short">
                            <Shield className="w-3.5 h-3.5 text-[#FF4D00]" />
                            <span className="text-[11px] font-mono-sf uppercase tracking-widest text-[#FF4D00]">{t.rev_disclaimer_short}</span>
                        </div>
                    </div>

                    <div className="mt-12 grid md:grid-cols-3 gap-4">
                        {t.rev_cases.map((c, i) => {
                            const Icon = USE_CASE_ICONS[i] || Target;
                            const color = USE_CASE_COLORS[i] || "#FF4D00";
                            return (
                                <div key={i} data-testid={`use-case-${i}`} className="bg-gradient-to-b from-[#0B111B] to-[#050914] border border-[#253046] hover:border-[#FF4D00]/30 rounded-lg p-6 flex flex-col kinetic">
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 rounded-lg grid place-items-center shrink-0" style={{ background: `${color}1A`, border: `1px solid ${color}55` }}>
                                            <Icon className="w-6 h-6" style={{ color }} />
                                        </div>
                                        <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">0{i + 1} · {t.rev_case_label}</div>
                                    </div>
                                    <h3 className="font-display text-xl font-black uppercase leading-tight text-white">{c.title}</h3>
                                    <p className="mt-4 text-sm text-slate-300 leading-relaxed flex-1">{c.body}</p>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-10 text-center text-xs text-slate-500 italic max-w-3xl mx-auto" data-testid="use-cases-disclaimer-long">
                        {t.rev_disclaimer_long}
                    </p>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="relative py-24 border-t border-[#253046] bg-gradient-to-b from-[#050914] via-[#080B10] to-[#050914]">
                <div className="max-w-6xl mx-auto px-4 sm:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="overline">{t.pri_kicker}</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">{t.pri_h2}</h2>
                        <p className="mt-4 text-slate-400">{t.pri_lead}</p>
                    </div>

                    <div className="mt-14 grid md:grid-cols-3 gap-4">
                        {/* One Shot 9€ */}
                        <div data-testid="pricing-oneshot" className="relative rounded-xl border border-[#253046] bg-[#0B111B] p-6 flex flex-col kinetic hover:border-[#FF4D00]/30">
                            <div className="overline">One-time</div>
                            <div className="font-display text-2xl font-bold uppercase mt-2">{t.pri_one_name}</div>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-display text-5xl font-black">{t.pri_one_price}€</span>
                                <span className="text-slate-500 text-xs">{t.pri_one_note}</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-400">{t.pri_one_sub}</p>
                            <ul className="mt-5 space-y-2 flex-1">
                                {t.pri_one_features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
                                        <Check className="w-4 h-4 mt-0.5 text-[#10B981] shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid="pricing-oneshot-cta"
                                onClick={() => buyPlan("oneshot")}
                                className="mt-6 w-full py-3 rounded-md font-semibold bg-[#0F141C] border border-[#253046] hover:border-[#FF4D00] text-white kinetic"
                            >
                                {t.pri_one_cta}
                            </button>
                        </div>

                        {/* Creator 39€ — Most popular */}
                        <div data-testid="pricing-creator" className="relative rounded-xl border-2 border-[#FF4D00] bg-gradient-to-b from-[#1E2738] via-[#161D2A] to-[#0B111B] p-6 flex flex-col kinetic flame-glow">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 flame-gradient text-black text-[10px] font-bold tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                                <Award className="w-3 h-3" /> {t.pri_reco}
                            </div>
                            <div className="overline">Subscription</div>
                            <div className="font-display text-2xl font-bold uppercase mt-2">{t.pri_mo_name}</div>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-display text-5xl font-black flame-text">{t.pri_mo_price}€</span>
                                <span className="text-slate-400 text-xs">{t.pri_mo_note}</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-300">{t.pri_mo_sub}</p>
                            <div className="mt-4 text-[11px] font-mono-sf uppercase tracking-widest text-[#FF4D00]">{t.pri_mo_features_head}</div>
                            <ul className="mt-2 space-y-2 flex-1">
                                {t.pri_mo_features.map((f) => {
                                    const isSoon = /coming soon|bientôt|próximamente/i.test(f);
                                    return (
                                        <li key={f} className={`flex items-start gap-2 text-sm ${isSoon ? "text-slate-400" : "text-slate-100"}`}>
                                            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isSoon ? "text-slate-500" : "text-[#FF4D00]"}`} />
                                            <span>{f}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <button
                                data-testid="pricing-creator-cta"
                                onClick={() => buyPlan("creator", 1)}
                                className="mt-6 w-full py-3 rounded-md font-semibold flame-gradient text-black hover:brightness-110 kinetic"
                            >
                                {t.pri_mo_cta}
                            </button>
                        </div>

                        {/* Capacity Pack +19€ */}
                        <div data-testid="pricing-pack" className="relative rounded-xl border border-[#253046] bg-[#0B111B] p-6 flex flex-col kinetic hover:border-[#FF4D00]/30">
                            <div className="overline">Add-on</div>
                            <div className="font-display text-2xl font-bold uppercase mt-2">{t.pri_pack_name}</div>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-display text-5xl font-black">{t.pri_pack_price}€</span>
                                <span className="text-slate-500 text-xs">{t.pri_pack_note}</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-400">{t.pri_pack_sub}</p>
                            <div className="mt-5 flex-1 space-y-2">
                                {t.pri_pack_bullets.map((b) => (
                                    <div key={b} className="flex items-start gap-2 text-sm text-slate-200">
                                        <PlusCircle className="w-4 h-4 mt-0.5 text-[#FF4D00] shrink-0" />
                                        <span>{b}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                data-testid="pricing-pack-cta"
                                onClick={() => buyPlan("creator", 2)}
                                className="mt-6 w-full py-3 rounded-md font-semibold bg-[#0F141C] border border-[#253046] hover:border-[#FF4D00] text-white kinetic"
                            >
                                {t.pri_pack_cta}
                            </button>
                        </div>
                    </div>

                    {/* Interactive calculator */}
                    <div data-testid="pricing-calculator" className="mt-14 rounded-2xl border border-[#253046] bg-gradient-to-r from-[#0F141C] via-[#0B111B] to-[#0F141C] p-6 sm:p-8">
                        <div className="flex items-center gap-2 overline">
                            <Calculator className="w-3.5 h-3.5 text-[#FF4D00]" />
                            <span>{t.pri_calc_kicker}</span>
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl font-black uppercase mt-2 leading-tight">
                            {t.pri_calc_h3}
                        </h3>

                        <div className="mt-6 flex flex-wrap gap-2">
                            {[1, 2, 3, 4, 5, 6].map((n) => {
                                const on = calcUnits === n;
                                return (
                                    <button
                                        key={n}
                                        data-testid={`calc-pick-${n * 30}`}
                                        onClick={() => setCalcUnits(n)}
                                        className={`flex-1 min-w-[80px] py-3 rounded-md font-mono-sf text-sm tracking-widest kinetic border ${on ? "border-[#FF4D00] bg-[#FF4D00]/10 text-white" : "border-[#253046] bg-[#0F141C] text-slate-400 hover:border-[#FF4D00]/50"}`}
                                    >
                                        <div className="font-display text-lg font-black">{n * 30}</div>
                                        <div className="text-[9px] uppercase text-slate-500">Shorts</div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-8 grid md:grid-cols-3 gap-6 items-center">
                            <div>
                                <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.pri_calc_units_label}</div>
                                <div data-testid="calc-shorts" className="font-display text-4xl font-black text-white mt-1">{calcUnits * 30}</div>
                                <div className="text-[10px] font-mono-sf text-slate-500 mt-1">
                                    {calcUnits === 1 ? "1 pack" : `${calcUnits} packs`} · {calcUnits} × 30
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.pri_calc_price_label}</div>
                                <div data-testid="calc-price" className="font-display text-5xl sm:text-6xl font-black flame-text mt-1">
                                    {(39 + Math.max(0, calcUnits - 1) * 19)}€
                                </div>
                                <div className="text-[10px] font-mono-sf text-slate-500 mt-1">/mois</div>
                            </div>
                            <div>
                                <button
                                    data-testid="calc-cta"
                                    onClick={() => buyPlan("creator", calcUnits)}
                                    className="w-full py-3.5 rounded-md font-semibold flame-gradient text-black hover:brightness-110 kinetic flex items-center justify-center gap-2"
                                >
                                    <Zap className="w-4 h-4" fill="currentColor" /> {t.pri_calc_cta}
                                </button>
                                <p className="mt-3 text-xs text-slate-500 leading-relaxed">{t.pri_calc_hint}</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap justify-center gap-6 text-xs font-mono-sf text-slate-500 uppercase tracking-widest">
                        <span className="flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5 text-[#FF4D00]" /> {lang === "fr" ? "Nouveau · Lancement 2026" : lang === "es" ? "Nuevo · Lanzamiento 2026" : "New · 2026 launch"}</span>
                        <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {lang === "fr" ? "Paiement sécurisé Stripe" : lang === "es" ? "Pago seguro Stripe" : "Secure Stripe checkout"}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {lang === "fr" ? "Accès instantané" : lang === "es" ? "Acceso instantáneo" : "Instant access"}</span>
                    </div>
                </div>
            </section>

            {/* ROI */}
            <section id="roi" className="relative py-24 border-t border-[#253046] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[#050914] via-[#080B10] to-[#050914]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FF4D00]/8 blur-[120px] pointer-events-none" />

                <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
                    {/* Header + calc */}
                    <div className="max-w-3xl">
                        <div className="overline">{t.roi_kicker}</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">
                            {t.roi_h2}
                        </h2>
                        <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed">{t.roi_lead}</p>
                    </div>

                    {/* Visual calc */}
                    <div data-testid="roi-calc" className="mt-10 rounded-2xl border border-[#253046] bg-gradient-to-r from-[#0B111B] via-[#0F141C] to-[#0B111B] p-6 sm:p-8">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 font-mono-sf">
                            <div className="text-center">
                                <div className="font-display text-4xl sm:text-5xl font-black flame-text leading-none">{t.roi_calc_a}</div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">{t.roi_calc_label_a}</div>
                            </div>
                            <Divide className="w-6 h-6 text-slate-600 shrink-0" />
                            <div className="text-center">
                                <div className="font-display text-4xl sm:text-5xl font-black text-white leading-none">{t.roi_calc_b}</div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">{t.roi_calc_label_b}</div>
                            </div>
                            <div className="text-slate-600 font-display text-3xl">=</div>
                            <div className="text-center">
                                <div className="font-display text-4xl sm:text-5xl font-black flame-text leading-none">{t.roi_calc_c}</div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">{t.roi_calc_label_c}</div>
                            </div>
                        </div>
                        <p className="mt-6 max-w-3xl mx-auto text-center text-sm sm:text-base text-slate-300 leading-relaxed">
                            {t.roi_note}
                        </p>
                    </div>

                    {/* 3 dimensions */}
                    <div className="mt-16 grid md:grid-cols-3 gap-4">
                        {[
                            { icon: Hourglass, title: t.roi_time_title,  desc: t.roi_time_desc,  tag: "01",  testid: "roi-dim-time" },
                            { icon: Rocket,    title: t.roi_reach_title, desc: t.roi_reach_desc, tag: "02",  testid: "roi-dim-reach" },
                            { icon: Coins,     title: t.roi_biz_title,   desc: t.roi_biz_desc,   tag: "03",  testid: "roi-dim-biz" },
                        ].map(({ icon: Icon, title, desc, tag, testid }) => (
                            <div key={tag} data-testid={testid} className="group relative bg-[#0B111B] border border-[#253046] hover:border-[#FF4D00]/50 rounded-lg p-6 kinetic overflow-hidden">
                                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#FF4D00]/5 group-hover:bg-[#FF4D00]/15 transition-colors" />
                                <div className="font-mono-sf text-[10px] text-slate-500">{tag}</div>
                                <Icon className="w-8 h-8 mt-3 text-[#FF4D00]" strokeWidth={1.6} />
                                <div className="font-display uppercase mt-4 text-lg sm:text-xl font-bold leading-tight">{title}</div>
                                <div className="text-sm text-slate-400 mt-3 leading-relaxed">{desc}</div>
                            </div>
                        ))}
                    </div>

                    {/* Paths to ROI */}
                    <div className="mt-20">
                        <div className="overline">{t.roi_paths_kicker}</div>
                        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black uppercase mt-3 leading-tight max-w-3xl">
                            {t.roi_paths_h3}
                        </h3>

                        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {t.roi_paths.map((p, i) => {
                                const Icon = [Youtube, Link2, Package, Target][i];
                                return (
                                    <div key={p.title} data-testid={`roi-path-${i}`} className="rounded-lg border border-[#253046] bg-[#0B111B] hover:border-[#FF4D00]/40 p-5 kinetic flex flex-col">
                                        <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#FF4D00]/10 text-[#FF4D00]">
                                            <Icon className="w-5 h-5" strokeWidth={1.8} />
                                        </div>
                                        <div className="font-display uppercase mt-4 text-base font-bold">{p.title}</div>
                                        <div className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{p.desc}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Illustrative examples */}
                    <div className="mt-20">
                        <div className="flex items-center gap-3">
                            <Calculator className="w-4 h-4 text-[#FF4D00]" />
                            <div className="overline">{t.roi_examples_kicker}</div>
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl font-black uppercase mt-3 leading-tight">
                            {t.roi_examples_h3}
                        </h3>

                        <div className="mt-8 grid md:grid-cols-3 gap-4">
                            {t.roi_examples.map((ex, i) => (
                                <div key={i} data-testid={`roi-example-${i}`} className="rounded-lg border border-dashed border-[#FF4D00]/30 bg-gradient-to-b from-[#161D2A] to-[#0B111B] p-5">
                                    <div className="text-xs font-mono-sf uppercase tracking-widest text-slate-500">{ex.top}</div>
                                    <div className="mt-3 font-display text-xl sm:text-2xl font-black flame-text leading-tight">{ex.bot}</div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-5 text-xs text-slate-500 italic leading-relaxed max-w-3xl">
                            {t.roi_examples_disclaimer}
                        </p>
                    </div>

                    {/* Interactive simulator */}
                    <div data-testid="roi-simulator" className="mt-16 rounded-2xl border border-[#253046] bg-gradient-to-br from-[#0F141C] via-[#0B111B] to-[#050914] p-6 sm:p-8">
                        <div className="flex items-center gap-2 overline">
                            <Calculator className="w-3.5 h-3.5 text-[#FF4D00]" />
                            <span>{t.sim_kicker}</span>
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl font-black uppercase mt-2 leading-tight">
                            {t.sim_h3}
                        </h3>

                        <div className="mt-8 grid lg:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-baseline justify-between mb-2">
                                        <label className="text-xs font-mono-sf uppercase tracking-widest text-slate-400">{t.sim_price_label}</label>
                                        <span data-testid="sim-price-value" className="font-display text-2xl font-black flame-text">{simPrice} €</span>
                                    </div>
                                    <input
                                        data-testid="sim-price-slider"
                                        type="range"
                                        min="5"
                                        max="500"
                                        step="1"
                                        value={simPrice}
                                        onChange={(e) => setSimPrice(Number(e.target.value))}
                                        className="w-full accent-[#FF4D00] cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] font-mono-sf text-slate-600 mt-1">
                                        <span>5 €</span><span>500 €</span>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-baseline justify-between mb-2">
                                        <label className="text-xs font-mono-sf uppercase tracking-widest text-slate-400">{t.sim_conv_label}</label>
                                        <span data-testid="sim-conv-value" className="font-display text-2xl font-black flame-text">{simConv}</span>
                                    </div>
                                    <input
                                        data-testid="sim-conv-slider"
                                        type="range"
                                        min="0"
                                        max="60"
                                        step="1"
                                        value={simConv}
                                        onChange={(e) => setSimConv(Number(e.target.value))}
                                        className="w-full accent-[#FF4D00] cursor-pointer"
                                    />
                                    <div className="flex justify-between text-[10px] font-mono-sf text-slate-600 mt-1">
                                        <span>0</span><span>60</span>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl border-2 border-[#FF4D00]/40 bg-gradient-to-b from-[#161D2A] to-[#0B111B] p-6 flex flex-col justify-center">
                                <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.sim_revenue_label}</div>
                                <div data-testid="sim-revenue" className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-black flame-text leading-none">
                                    {simRevenue.toLocaleString(lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US")} €
                                </div>
                                <div className="mt-4 flex items-baseline gap-2">
                                    <span data-testid="sim-ratio" className="font-display text-xl font-black text-white">×{simRatio.toFixed(1)}</span>
                                    <span className="text-xs font-mono-sf uppercase tracking-widest text-slate-500">{t.sim_ratio_label}</span>
                                </div>
                                <div className="mt-4 h-1.5 rounded-full bg-[#161D2A] overflow-hidden">
                                    <div
                                        className="h-full flame-gradient transition-all duration-300"
                                        style={{ width: `${Math.min(100, (simRatio / 20) * 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="mt-6 text-xs text-slate-500 italic leading-relaxed max-w-3xl">
                            {t.sim_disclaimer}
                        </p>
                    </div>

                    {/* Strong closing */}
                    <div data-testid="roi-closing" className="mt-20 relative rounded-2xl border-2 border-[#FF4D00]/60 bg-gradient-to-br from-[#1E2738] via-[#161D2A] to-[#0B111B] p-8 sm:p-12 flame-glow overflow-hidden">
                        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FF4D00]/15 blur-3xl" />
                        <div className="relative">
                            <p className="font-display text-xl sm:text-2xl lg:text-3xl font-black uppercase leading-tight text-slate-200">
                                {t.roi_closing_a}
                            </p>
                            <p className="mt-4 font-display text-xl sm:text-2xl lg:text-3xl font-black uppercase leading-tight">
                                <span className="flame-text">{t.roi_closing_b}</span>
                            </p>
                            <p className="mt-8 font-mono-sf text-sm sm:text-base uppercase tracking-widest text-slate-400">
                                {t.roi_closing_verbs}
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <button
                                    data-testid="roi-cta-monthly"
                                    onClick={() => buyPlan("creator", 1)}
                                    className="flame-gradient text-black font-semibold px-6 py-3.5 rounded-md flex items-center gap-2 kinetic flame-glow"
                                >
                                    <Zap className="w-4 h-4" fill="currentColor" /> {t.roi_cta_primary} <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    data-testid="roi-cta-unique"
                                    onClick={() => buyPlan("oneshot")}
                                    className="px-5 py-3.5 rounded-md text-slate-200 hover:text-white border border-[#253046] hover:border-[#FF4D00]/60 bg-[#0F141C] kinetic text-sm"
                                >
                                    {t.roi_cta_secondary}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative py-24 border-t border-[#253046]">
                <div className="max-w-3xl mx-auto px-4 sm:px-8">
                    <div className="overline">{t.faq_kicker}</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase mt-3 leading-tight">{t.faq_h2}</h2>

                    <div className="mt-10 space-y-2">
                        {(FAQ[lang] || FAQ.fr).map((it, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} data-testid={`faq-item-${i}`} className={`rounded-lg border ${open ? "border-[#FF4D00]/50 bg-gradient-to-b from-[#1E2738] to-[#0B111B]" : "border-[#253046] bg-[#0B111B]"} overflow-hidden kinetic`}>
                                    <button
                                        data-testid={`faq-toggle-${i}`}
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-4 p-5 text-left"
                                    >
                                        <div className="flex items-start gap-3">
                                            <HelpCircle className={`w-4 h-4 mt-0.5 shrink-0 ${open ? "text-[#FF4D00]" : "text-slate-500"}`} />
                                            <span className="font-display uppercase text-sm sm:text-base font-bold text-white">{it.q}</span>
                                        </div>
                                        {open
                                            ? <MinusCircle className="w-5 h-5 text-[#FF4D00] shrink-0" />
                                            : <PlusCircle className="w-5 h-5 text-slate-500 shrink-0" />}
                                    </button>
                                    {open && (
                                        <div className="px-5 pb-5 pl-12 text-sm text-slate-300 leading-relaxed">
                                            {it.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section id="join" className="relative py-24 border-t border-[#253046] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#080B10] via-[#0F141C] to-[#050914]" />
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#FF4D00]/10 blur-[100px]" />

                <div className="relative max-w-4xl mx-auto px-4 sm:px-8 text-center">
                    <div className="overline">{t.end_kicker}</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase mt-3 leading-tight max-w-3xl mx-auto">
                        {t.end_h2}
                    </h2>
                    <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                        {t.end_sub}
                    </p>

                    <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-300">
                        {t.end_bullets.map((b, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-[#10B981]" /> <span>{b}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-wrap justify-center items-center gap-4">
                        <button
                            data-testid="final-cta"
                            onClick={() => buyPlan("oneshot")}
                            className="flame-gradient text-black font-semibold px-8 py-4 rounded-md flex items-center gap-2 kinetic flame-glow text-base"
                        >
                            <Zap className="w-4 h-4" fill="currentColor" /> {t.end_cta}
                        </button>
                        <div data-testid="final-social-proof" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10">
                            <Rocket className="w-3.5 h-3.5 text-[#FFB800]" />
                            <span className="text-xs font-mono-sf text-[#FFB800] uppercase tracking-widest">{t.end_proof}</span>
                        </div>
                    </div>
                </div>
            </section>

            <EmailCapture lang={lang} source="landing_footer" />

            <TrailerModal open={trailerOpen} onClose={() => setTrailerOpen(false)} />

            <footer className="border-t border-[#253046] py-10 text-center text-xs text-slate-500 font-mono-sf" data-testid="landing-footer">
                <div>© {new Date().getFullYear()} Shorts Factory · {lang === "fr" ? "L'usine à YouTube Shorts" : lang === "es" ? "La fábrica de YouTube Shorts" : "The YouTube Shorts factory"}</div>
                <div className="mt-4 flex flex-wrap justify-center gap-5 uppercase tracking-widest">
                    <a href="/privacy" className="hover:text-[#FF4D00] kinetic" data-testid="footer-privacy-link">
                        {lang === "fr" ? "Politique de confidentialité" : lang === "es" ? "Política de privacidad" : "Privacy policy"}
                    </a>
                    <a href="/terms" className="hover:text-[#FF4D00] kinetic" data-testid="footer-terms-link">
                        {lang === "fr" ? "Conditions d'utilisation (brouillon)" : lang === "es" ? "Condiciones de uso (borrador)" : "Terms of use (draft)"}
                    </a>
                </div>
            </footer>
        </div>
    );
}
